// Checkout order lines + payment summary + note card
import {
  buildPresetSpecs,
  calcDiscountPercent,
  escapeAttr,
  formatGroupedInt,
  formatPriceWithRub,
  formatRub,
  parsePriceNumber,
  presetSpecsPairs,
  type SpecsSource
} from '../../core/format.js';
import { lazyImageHtml, refreshLazyImages } from '../../core/lazy-image.js';
import {
  loadCart,
  pruneCartAgainstPresets,
  cartTotalsRub,
  cartItemsPlural,
  type CartTotalsRub
} from '../cart/cart.js';
import { fetchPresets, getLastPresetsList, setLastPresetsList } from '../presets/list.js';
import type { CartLine, PresetItem } from '../../types/api.js';
import { parseTemplateIdFromCartLineId } from '../../lib/template-config.js';
import {
  getCheckoutCdekQuote,
  isCheckoutCdekPickerMode,
  isCheckoutCdekQuoteLoading,
  refreshCheckoutCdekQuote
} from './cdek.js';

let checkoutPresetHydrateCartKey = '';
let checkoutPresetHydrateScheduled = false;

function checkoutSpecsHasAny(specsObj: SpecsSource): boolean {
  const pairs = presetSpecsPairs(specsObj);
  for (let i = 0; i < pairs.length; i++) {
    if (pairs[i][1]) return true;
  }
  return false;
}

function getSpecsForCheckoutLine(line: CartLine): SpecsSource {
  if (line?.specs && checkoutSpecsHasAny(line.specs)) return line.specs;
  const fromLine = buildPresetSpecs(line?.specs ? line.specs : line);
  const pid =
    line?.templateId != null
      ? String(line.templateId)
      : parseTemplateIdFromCartLineId(String(line?.id || ''));
  if (!pid) return fromLine;
  const presetsList = getLastPresetsList();
  if (!Array.isArray(presetsList)) return fromLine;
  for (let i = 0; i < presetsList.length; i++) {
    if (String(presetsList[i].id) === pid) {
      return presetsList[i].specs || fromLine;
    }
  }
  return fromLine;
}

function buildCheckoutOrderSpecsHtml(specsObj: SpecsSource): string {
  const pairs = presetSpecsPairs(specsObj);
  const rows: string[] = [];
  for (let i = 0; i < pairs.length; i++) {
    const label = pairs[i][0];
    const value = pairs[i][1];
    if (!value) continue;
    rows.push(
      '<div class="checkout-order-spec-row">' +
        '<span class="checkout-order-spec-label">' +
        escapeAttr(label) +
        '</span>' +
        '<span class="checkout-order-spec-value">' +
        escapeAttr(value) +
        '</span>' +
        '</div>'
    );
  }
  if (!rows.length) return '';
  return '<div class="checkout-order-specs">' + rows.join('') + '</div>';
}

function checkoutCartNeedsPresetFetch(cart: CartLine[]): boolean {
  if (!cart.length) return false;
  const map: Record<string, boolean> = {};
  const presetsList = getLastPresetsList();
  if (!Array.isArray(presetsList)) return false;
  for (let i = 0; i < presetsList.length; i++) {
    map[String(presetsList[i].id)] = true;
  }
  for (let j = 0; j < cart.length; j++) {
    const tid =
      cart[j].templateId != null
        ? String(cart[j].templateId)
        : parseTemplateIdFromCartLineId(String(cart[j].id));
    if (!map[tid]) return true;
  }
  return false;
}

export function refreshCheckoutOrderSummary(): void {
  const root = document.getElementById('checkout-order-lines');
  if (!root) return;
  try {
    pruneCartAgainstPresets();
  } catch {
    /* ignore */
  }
  const cart = loadCart();
  if (!cart.length) {
    root.innerHTML = '<p class="checkout-order-empty">В корзине пока нет товаров.</p>';
    try {
      document.dispatchEvent(new CustomEvent('checkout-order-lines-changed'));
    } catch {
      /* ignore */
    }
    try {
      refreshCheckoutPaymentSummary();
    } catch {
      /* ignore */
    }
    return;
  }
  root.innerHTML = cart
    .map((line) => {
      const qty = line.qty || 1;
      const priceStr = formatPriceWithRub(line.price);
      const oldStr = line.oldPrice ? formatPriceWithRub(line.oldPrice) : '';
      const discountPercent = calcDiscountPercent(line.price, line.oldPrice);
      const priceDisplay = priceStr || '—';
      let priceRowHtml = '<div class="preset-card-price-row">';
      priceRowHtml += '<p class="preset-card-price">' + escapeAttr(priceDisplay) + '</p>';
      if (oldStr && oldStr !== priceStr) {
        priceRowHtml += '<p class="preset-card-old-price">' + escapeAttr(oldStr) + '</p>';
      }
      if (discountPercent) {
        priceRowHtml += '<p class="preset-card-discount">-' + discountPercent + '%</p>';
      }
      priceRowHtml += '</div>';
      const thumb = line.image
        ? lazyImageHtml(line.image, { loading: 'lazy' })
        : '<div class="checkout-order-thumb-placeholder"></div>';
      const qtyLabel = qty + ' шт.';
      const specsHtml = buildCheckoutOrderSpecsHtml(getSpecsForCheckoutLine(line));
      let bodyHtml = '';
      if (specsHtml) {
        bodyHtml = '<div class="checkout-order-body">' + specsHtml + '</div>';
      }
      return (
        '<div class="checkout-order-line" data-cart-id="' +
        escapeAttr(line.id) +
        '">' +
        '<div class="checkout-order-media">' +
        '<div class="checkout-order-thumb">' +
        thumb +
        '<span class="checkout-order-qty">' +
        escapeAttr(qtyLabel) +
        '</span></div>' +
        '<div class="checkout-order-info">' +
        '<p class="preset-card-title">' +
        escapeAttr(line.title || '') +
        '</p>' +
        priceRowHtml +
        '</div>' +
        '</div>' +
        bodyHtml +
        '</div>'
      );
    })
    .join('');
  refreshLazyImages(root);
  try {
    document.dispatchEvent(new CustomEvent('checkout-order-lines-changed'));
  } catch {
    /* ignore */
  }

  const cartKey = cart
    .map((x) => String(x.id))
    .sort()
    .join('|');
  if (cartKey !== checkoutPresetHydrateCartKey) {
    checkoutPresetHydrateCartKey = cartKey;
    checkoutPresetHydrateScheduled = false;
  }
  if (cart.length && !checkoutPresetHydrateScheduled && checkoutCartNeedsPresetFetch(cart)) {
    checkoutPresetHydrateScheduled = true;
    fetchPresets()
      .then((items) => {
        if (!items || !Array.isArray(items)) return;
        setLastPresetsList(
          items.filter((item: PresetItem) => !item || item.hidden !== true)
        );
        refreshCheckoutOrderSummary();
      })
      .catch(() => {
        /* ignore */
      });
  }
  try {
    refreshCheckoutCdekQuote();
    refreshCheckoutPaymentSummary();
  } catch {
    /* ignore */
  }
}

interface CheckoutPaymentBreakdown extends CartTotalsRub {
  sumNet: number;
  sumGross: number;
  discount: number;
}

function computeCheckoutPaymentBreakdown(): CheckoutPaymentBreakdown {
  const cart = loadCart();
  const totals = cartTotalsRub(cart);
  let sumGross = 0;
  for (let i = 0; i < cart.length; i++) {
    const line = cart[i];
    const q = line.qty || 1;
    const cur = parsePriceNumber(line.price);
    const old = parsePriceNumber(line.oldPrice);
    if (cur != null) {
      if (old != null && old > cur) {
        sumGross += old * q;
      } else {
        sumGross += cur * q;
      }
    }
  }
  let discount = 0;
  if (totals.allPriced && totals.count > 0) {
    discount = sumGross - totals.sum;
    if (discount < 1) discount = 0;
  }
  return {
    count: totals.count,
    sum: totals.sum,
    allPriced: totals.allPriced,
    sumNet: totals.sum,
    sumGross,
    discount
  };
}

export function refreshCheckoutPaymentSummary(): void {
  const itemsLabelEl = document.getElementById('checkout-pay-items-label');
  const itemsValueEl = document.getElementById('checkout-pay-items-value');
  const discountRowEl = document.getElementById('checkout-pay-discount-row');
  const discountValueEl = document.getElementById('checkout-pay-discount-value');
  const deliveryValueEl = document.getElementById('checkout-pay-delivery-value');
  const totalValueEl = document.getElementById('checkout-pay-total-value');
  if (!itemsLabelEl || !itemsValueEl || !totalValueEl) return;

  const bd = computeCheckoutPaymentBreakdown();
  itemsLabelEl.textContent = bd.count + ' ' + cartItemsPlural(bd.count) + ' на сумму';

  if (!bd.count) {
    itemsValueEl.textContent = '—';
    totalValueEl.textContent = '—';
    if (discountRowEl) discountRowEl.hidden = true;
    if (deliveryValueEl) {
      deliveryValueEl.textContent = '—';
      deliveryValueEl.classList.add('checkout-pay-value--muted');
    }
    return;
  }

  if (!bd.allPriced) {
    itemsValueEl.textContent = '—';
    totalValueEl.textContent = '—';
    if (discountRowEl) discountRowEl.hidden = true;
    if (deliveryValueEl) {
      deliveryValueEl.textContent = '—';
      deliveryValueEl.classList.add('checkout-pay-value--muted');
    }
    return;
  }

  itemsValueEl.textContent = formatRub(bd.sumGross);

  if (bd.discount > 0 && discountRowEl && discountValueEl) {
    discountRowEl.hidden = false;
    discountValueEl.textContent = '− ' + formatGroupedInt(Math.round(bd.discount)) + ' ₽';
  } else if (discountRowEl) {
    discountRowEl.hidden = true;
  }

  totalValueEl.textContent = formatRub(bd.sumNet);

  if (deliveryValueEl) {
    const methodEl = document.querySelector(
      'input[name="checkout-delivery-method"]:checked'
    ) as HTMLInputElement | null;
    const method = methodEl ? String(methodEl.value || '') : '';
    if (method === 'self-pickup') {
      deliveryValueEl.textContent = 'Не включена в заказ';
      deliveryValueEl.classList.add('checkout-pay-value--muted');
      totalValueEl.textContent = formatRub(bd.sumNet);
      return;
    }

    if (isCheckoutCdekPickerMode()) {
      const quote = getCheckoutCdekQuote();
      if (isCheckoutCdekQuoteLoading()) {
        deliveryValueEl.textContent = 'Расчёт…';
        deliveryValueEl.classList.add('checkout-pay-value--muted');
        totalValueEl.textContent = formatRub(bd.sumNet);
        return;
      }
      if (quote && Number.isFinite(quote.deliveryRub)) {
        deliveryValueEl.textContent = formatRub(quote.deliveryRub);
        deliveryValueEl.classList.remove('checkout-pay-value--muted');
        totalValueEl.textContent = formatRub(bd.sumNet + quote.deliveryRub);
        return;
      }
      deliveryValueEl.textContent = 'Выберите ПВЗ';
      deliveryValueEl.classList.add('checkout-pay-value--muted');
      totalValueEl.textContent = formatRub(bd.sumNet);
      return;
    }

    deliveryValueEl.classList.add('checkout-pay-value--muted');
    deliveryValueEl.textContent = 'Уточним при обработке';
  }
}

export function bindCheckoutNoteCardExpansion(
  noteCard: HTMLElement | null,
  noteMoreBtn: HTMLElement | null
): void {
  if (!noteCard || !noteMoreBtn) return;
  const card = noteCard;
  const moreBtn = noteMoreBtn;
  card.classList.add('checkout-note-card-show-more');

  let interactionGen = 0;
  let collapseTimer: ReturnType<typeof setTimeout> | null = null;
  let collapseOnEndHandler: ((e: Event) => void) | null = null;

  function abortPending(textEl: HTMLElement): void {
    if (collapseTimer !== null) {
      try {
        clearTimeout(collapseTimer);
      } catch {
        /* ignore */
      }
      collapseTimer = null;
    }
    if (collapseOnEndHandler !== null) {
      try {
        textEl.removeEventListener('transitionend', collapseOnEndHandler);
      } catch {
        /* ignore */
      }
      collapseOnEndHandler = null;
    }
  }

  moreBtn.addEventListener('click', (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
    } catch {
      /* ignore */
    }
    const textEl = card.querySelector('.checkout-note-text') as HTMLElement | null;
    if (!textEl) return;

    abortPending(textEl);
    interactionGen += 1;
    const ticket = interactionGen;

    try {
      const startH = textEl.getBoundingClientRect().height || 0;
      textEl.style.setProperty('--checkout-note-max-h', startH + 'px');
      void textEl.offsetHeight;
    } catch {
      /* ignore */
    }

    card.classList.add('checkout-note-card-expanded');
    card.classList.remove('checkout-note-card-show-more');

    try {
      const fullH = textEl.scrollHeight || 0;
      requestAnimationFrame(() => {
        if (ticket !== interactionGen) return;
        textEl.style.setProperty('--checkout-note-max-h', fullH + 'px');
      });
    } catch {
      /* ignore */
    }
  });

  card.addEventListener('click', (e) => {
    try {
      if (e.target && moreBtn.contains(e.target as Node)) return;
    } catch {
      /* ignore */
    }
    if (!card.classList.contains('checkout-note-card-expanded')) return;
    const textEl = card.querySelector('.checkout-note-text') as HTMLElement | null;
    if (!textEl) return;
    const noteText = textEl;

    abortPending(noteText);
    interactionGen += 1;
    const ticket = interactionGen;

    card.classList.add('checkout-note-card-show-more');

    let lh = 0;
    let collapsedH = 0;
    try {
      lh = parseFloat(getComputedStyle(noteText).lineHeight || '0') || 0;
      collapsedH = lh ? Math.ceil(lh * 3) : 0;
    } catch {
      /* ignore */
    }
    if (!collapsedH) {
      card.classList.remove('checkout-note-card-expanded');
      return;
    }

    let fullH = 0;
    try {
      fullH = noteText.scrollHeight || 0;
    } catch {
      /* ignore */
    }
    if (!fullH) {
      card.classList.remove('checkout-note-card-expanded');
      return;
    }

    try {
      noteText.style.setProperty('--checkout-note-max-h', fullH + 'px');
      void noteText.offsetHeight;
    } catch {
      /* ignore */
    }

    let folded = false;

    function finishCollapse(): void {
      if (folded || ticket !== interactionGen) return;
      folded = true;
      if (collapseTimer !== null) {
        try {
          clearTimeout(collapseTimer);
        } catch {
          /* ignore */
        }
        collapseTimer = null;
      }
      if (collapseOnEndHandler !== null) {
        try {
          noteText.removeEventListener('transitionend', collapseOnEndHandler);
        } catch {
          /* ignore */
        }
        collapseOnEndHandler = null;
      }
      card.classList.remove('checkout-note-card-expanded');
      try {
        noteText.style.setProperty('--checkout-note-max-h', collapsedH + 'px');
      } catch {
        /* ignore */
      }
    }

    collapseOnEndHandler = (ev: Event) => {
      if (ticket !== interactionGen) return;
      if (ev && 'propertyName' in ev && ev.propertyName !== 'max-height') return;
      finishCollapse();
    };

    try {
      noteText.addEventListener('transitionend', collapseOnEndHandler);
    } catch {
      /* ignore */
    }

    collapseTimer = setTimeout(() => {
      collapseTimer = null;
      finishCollapse();
    }, 420);

    try {
      requestAnimationFrame(() => {
        if (ticket !== interactionGen) return;
        noteText.style.setProperty('--checkout-note-max-h', collapsedH + 'px');
      });
    } catch {
      try {
        noteText.style.setProperty('--checkout-note-max-h', collapsedH + 'px');
      } catch {
        /* ignore */
      }
    }
  });
}

import { getBuildOrderDraft } from '../../app/build-draft.js';

export function refreshBuildCheckoutOrderSummary(): void {
  const root = document.getElementById('checkout-order-lines');
  if (!root) return;
  const draft = getBuildOrderDraft();
  if (!draft || !draft.tasks) {
    root.innerHTML = '<p class="checkout-order-empty">Заполните форму сборки.</p>';
    return;
  }
  const lines = draft.tasks.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  root.innerHTML =
    '<div class="checkout-build-lines">' +
    lines
      .map((line) => {
        const colon = line.indexOf(':');
        if (colon > 0 && colon < 24) {
          const label = line.slice(0, colon).trim();
          const value = line.slice(colon + 1).trim();
          return (
            '<div class="checkout-build-line">' +
            '<span class="checkout-build-line-label">' +
            escapeAttr(label) +
            '</span>' +
            escapeAttr(value) +
            '</div>'
          );
        }
        return '<div class="checkout-build-line">' + escapeAttr(line) + '</div>';
      })
      .join('') +
    '</div>';
  try {
    document.dispatchEvent(new CustomEvent('checkout-order-lines-changed'));
  } catch {
    /* ignore */
  }
}
